import React from 'react';
import { Gallery, MediaItem } from '../types';
import { motion, AnimatePresence } from 'motion/react';
import { Play, Calendar, MapPin, X, ChevronLeft, ChevronRight, Image as ImageIcon, Video as VideoIcon, BookOpen, Search, Heart, Sparkles, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

interface GalleryGridProps {
  galleries: Gallery[];
  theme?: string;
  displayMode?: 'grid' | 'book';
  onToggleFavoriteMedia?: (galleryId: string, mediaId: string) => void;
}

export function GalleryGrid({ galleries, theme, displayMode = 'grid', onToggleFavoriteMedia }: GalleryGridProps) {
  const [selectedCategory, setSelectedCategory] = React.useState<string>('all');
  const [activeGallery, setActiveGallery] = React.useState<Gallery | null>(null);
  const [activeMediaIndex, setActiveMediaIndex] = React.useState<number | null>(null);
  const [currentAlbumPage, setCurrentAlbumPage] = React.useState<number>(0);
  const [isPageFlipping, setIsPageFlipping] = React.useState<boolean>(false);
  const [flipDirection, setFlipDirection] = React.useState<'next' | 'prev'>('next');
  const { lang, t } = useLanguage();

  // Sort galleries by order index
  const sortedGalleries = React.useMemo(() => {
    return [...galleries].sort((a, b) => {
      const orderA = typeof a.order === 'number' ? a.order : 999;
      const orderB = typeof b.order === 'number' ? b.order : 999;
      return orderA - orderB;
    });
  }, [galleries]);

  // Filter galleries
  const filteredGalleries = selectedCategory === 'all'
    ? sortedGalleries
    : sortedGalleries.filter(g => g.category === selectedCategory);

  // Reset book page on displayMode or category filter change
  React.useEffect(() => {
    if (displayMode === 'book') {
      setCurrentAlbumPage(0);
      setIsPageFlipping(false);
    }
  }, [displayMode]);

  // Reset book page on category filter change
  React.useEffect(() => {
    setCurrentAlbumPage(0);
  }, [selectedCategory]);

  const openGallery = (gallery: Gallery) => {
    setActiveGallery(gallery);
    setActiveMediaIndex(0); // Start with first media
  };

  const closeGallery = () => {
    setActiveGallery(null);
    setActiveMediaIndex(null);
  };

  const nextMedia = () => {
    if (activeGallery && activeMediaIndex !== null) {
      setActiveMediaIndex((activeMediaIndex + 1) % activeGallery.media.length);
    }
  };

  const prevMedia = () => {
    if (activeGallery && activeMediaIndex !== null) {
      setActiveMediaIndex(
        activeMediaIndex === 0 ? activeGallery.media.length - 1 : activeMediaIndex - 1
      );
    }
  };

  // Keyboard controls for lightbox
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!activeGallery) return;
      if (e.key === 'Escape') closeGallery();
      if (e.key === 'ArrowRight') nextMedia();
      if (e.key === 'ArrowLeft') prevMedia();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeGallery, activeMediaIndex]);

  // Lock body scroll when activeGallery lightbox is open
  React.useEffect(() => {
    if (activeGallery) {
      document.body.classList.add('overflow-hidden', 'touch-none', 'lightbox-active');
    } else {
      document.body.classList.remove('overflow-hidden', 'touch-none', 'lightbox-active');
    }
    return () => {
      document.body.classList.remove('overflow-hidden', 'touch-none', 'lightbox-active');
    };
  }, [activeGallery]);

  const activeMedia: MediaItem | null =
    activeGallery && activeMediaIndex !== null
      ? activeGallery.media[activeMediaIndex]
      : null;

  return (
    <section id="galleries" className="py-24 bg-theme-bg border-t border-theme-border relative z-10">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Section Title */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] text-theme-accent font-mono block mb-3">
              {theme === 'sherlock-wedding' ? '🔍 L\'Enquête des Sentiments' : t('portfolio_tag')}
            </span>
            <h2 className="text-3xl md:text-5xl font-mono text-theme-text font-extrabold tracking-tight">
              {theme === 'sherlock-wedding' ? 'Chroniques & Preuves d\'Amour' : t('portfolio_title')}
            </h2>
          </div>
          
          {/* Category Filter Pills */}
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'all', label: lang === 'fr' ? 'Tout explorer' : 'Explore All' },
              { id: 'mariage', label: lang === 'fr' ? 'Mariages' : 'Weddings' },
              { id: 'chateau', label: lang === 'fr' ? 'Châteaux' : 'Castles' },
              { id: 'domaine', label: lang === 'fr' ? 'Domaines' : 'Estates' }
            ].map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border transition-all duration-300 cursor-pointer ${
                  selectedCategory === cat.id
                    ? 'bg-theme-accent border-theme-accent text-theme-bg font-semibold'
                    : 'bg-theme-card border-theme-border text-theme-secondary hover:text-theme-text hover:border-theme-border-hover'
                }`}
                id={`filter-btn-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>



        {/* INTERACTIVE ALbum Book mode */}
        {displayMode === 'book' && (
          <div className="mb-24 relative max-w-5xl mx-auto">
            {/* Top Badge Info */}
            <div className="text-center mb-8">
              <span className={`px-3 py-1 border text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-2 ${
                theme === 'sherlock-wedding'
                  ? 'bg-amber-400/10 border-amber-400/30 text-amber-400'
                  : 'bg-theme-accent/10 border-theme-accent/30 text-theme-accent'
              }`}>
                <BookOpen className="w-3.5 h-3.5 animate-pulse" />
                {theme === 'sherlock-wedding' 
                  ? "Livre d'Or Interactif & Carnet d'Enquêtes" 
                  : "Album Numérique & Récit de Lumière"}
              </span>
              <p className="text-xs text-theme-secondary mt-2 font-mono">
                {lang === 'fr' 
                  ? "Tournez les pages pour explorer nos chapitres de reportages" 
                  : "Turn the pages to explore our story chapters"}
              </p>
            </div>

            {/* Book Body */}
            <div className={`relative min-h-[520px] md:min-h-[580px] p-4 sm:p-6 md:p-8 rounded-lg shadow-2xl transition-all duration-500 overflow-visible ${
              theme === 'sherlock-wedding'
                ? 'bg-[#1c1510] border-4 border-[#dfa95c]/20'
                : 'bg-theme-card border-4 border-theme-border'
            }`}>
              {/* Antique / Sleek binding center line */}
              <div className={`absolute inset-y-0 left-1/2 -translate-x-1/2 w-[30px] border-x hidden md:block z-20 pointer-events-none ${
                theme === 'sherlock-wedding'
                  ? 'bg-gradient-to-r from-black/50 via-black/20 to-black/50 border-[#dfa95c]/10'
                  : 'bg-gradient-to-r from-black/60 via-transparent to-black/60 border-theme-border/20'
              }`} />
              
              {/* Corner Ornaments */}
              {theme === 'sherlock-wedding' ? (
                <>
                  <div className="absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 border-[#dfa95c]/30 rounded-tl-sm pointer-events-none" />
                  <div className="absolute top-2 right-2 w-6 h-6 border-t-2 border-r-2 border-[#dfa95c]/30 rounded-tr-sm pointer-events-none" />
                  <div className="absolute bottom-2 left-2 w-6 h-6 border-b-2 border-l-2 border-[#dfa95c]/30 rounded-bl-sm pointer-events-none" />
                  <div className="absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 border-[#dfa95c]/30 rounded-br-sm pointer-events-none" />
                </>
              ) : (
                <>
                  <div className="absolute top-2 left-2 w-4 h-4 border-t border-l border-theme-accent/40 pointer-events-none" />
                  <div className="absolute top-2 right-2 w-4 h-4 border-t border-r border-theme-accent/40 pointer-events-none" />
                  <div className="absolute bottom-2 left-2 w-4 h-4 border-b border-l border-theme-accent/40 pointer-events-none" />
                  <div className="absolute bottom-2 right-2 w-4 h-4 border-b border-r border-theme-accent/40 pointer-events-none" />
                </>
              )}

              {/* Dynamic Page Container with Perspective */}
              <div className="relative w-full min-h-[480px] md:min-h-[520px] overflow-visible flex items-stretch" style={{ perspective: '2000px' }}>
                {/* Floating side arrows for easy page turning */}
                {currentAlbumPage > 0 && (
                  <button
                    onClick={() => {
                      if (isPageFlipping) return;
                      setFlipDirection('prev');
                      setCurrentAlbumPage(Math.max(0, currentAlbumPage - 1));
                    }}
                    className="absolute -left-3 sm:-left-6 top-1/2 -translate-y-1/2 z-40 p-2.5 sm:p-3.5 bg-amber-400 text-black shadow-2xl hover:bg-amber-300 transition-all rounded-full cursor-pointer hover:scale-110 active:scale-95"
                    aria-label="Tourner vers la page précédente de l'album"
                    title={lang === 'fr' ? 'Page Précédente' : 'Previous Page'}
                  >
                    <ChevronLeft className="w-5 h-5 font-bold" />
                  </button>
                )}

                {currentAlbumPage < filteredGalleries.length + 1 && (
                  <button
                    onClick={() => {
                      if (isPageFlipping) return;
                      setFlipDirection('next');
                      setCurrentAlbumPage(Math.min(filteredGalleries.length + 1, currentAlbumPage + 1));
                    }}
                    className="absolute -right-3 sm:-right-6 top-1/2 -translate-y-1/2 z-40 p-2.5 sm:p-3.5 bg-amber-400 text-black shadow-2xl hover:bg-amber-300 transition-all rounded-full cursor-pointer hover:scale-110 active:scale-95"
                    aria-label="Tourner vers la page suivante de l'album"
                    title={lang === 'fr' ? 'Page Suivante' : 'Next Page'}
                  >
                    <ChevronRight className="w-5 h-5 font-bold" />
                  </button>
                )}

                <AnimatePresence initial={false} custom={flipDirection} mode="wait">
                  <motion.div
                    key={currentAlbumPage}
                    custom={flipDirection}
                    variants={{
                      enter: (dir: 'next' | 'prev') => ({
                        x: dir === 'next' ? 40 : -40,
                        opacity: 0,
                      }),
                      center: {
                        x: 0,
                        opacity: 1,
                        transition: {
                          duration: 0.35,
                          ease: [0.25, 1, 0.5, 1],
                        }
                      },
                      exit: (dir: 'next' | 'prev') => ({
                        x: dir === 'next' ? -40 : 40,
                        opacity: 0,
                        transition: {
                          duration: 0.25,
                          ease: "easeIn"
                        }
                      })
                    }}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="w-full h-full relative"
                  >
                    {currentAlbumPage === 0 ? (
                      /* PAGE 0: COVER */
                      <div 
                        className="w-full h-full flex flex-col items-center justify-center text-center p-6 sm:p-10 relative rounded min-h-[420px] border-[#3e2723]/50 text-[#1e1712] shadow-[inset_0_0_60px_rgba(0,0,0,0.5)]"
                        style={{
                          backgroundColor: '#e6d5b8',
                          backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")',
                          backgroundSize: 'auto',
                          backgroundRepeat: 'repeat'
                        }}
                      >
                        <div className="absolute inset-4 border-2 rounded pointer-events-none border-[#5c4033]/30" />
                        <Sparkles className="w-10 h-10 text-[#5c4033] mb-6 opacity-80" />
                        <span className="text-[10px] font-mono text-[#5c4033] uppercase tracking-[0.3em] mb-2 font-bold">
                          {theme === 'sherlock-wedding' ? 'Volume Unique' : 'Chronicle Album'}
                        </span>
                        <h3 className="text-2xl sm:text-4xl font-serif tracking-wider mb-4 leading-tight max-w-2xl px-2">
                          {theme === 'sherlock-wedding' ? "LES ENQUÊTES DU CŒUR" : "LIFE IN LIGHT & MOTION"}
                        </h3>
                        <div className="w-24 h-[1px] bg-[#5c4033]/60 my-3" />
                        <p className="text-xs sm:text-sm italic max-w-md px-6 leading-relaxed mb-8 font-serif text-zinc-800">
                          {theme === 'sherlock-wedding'
                            ? '"Il est d\'une importance capitale d\'observer les détails les plus subtils d\'une journée de mariage, car c\'est là que réside la pure vérité des sentiments."'
                            : '"Capturing the exquisite poetry of light, space, and motion. Every couple is a distinct masterpiece, waiting to be chronicled with absolute truth."'}
                        </p>
                        
                        <button
                          onClick={() => {
                            setFlipDirection('next');
                            setCurrentAlbumPage(1);
                          }}
                          className="px-6 py-3 font-serif uppercase tracking-widest text-xs font-semibold hover:bg-[#3e2723] hover:text-[#f4ebd9] transition-all cursor-pointer shadow-lg hover:translate-y-[-1px] bg-[#1e1712] text-[#f4ebd9] border border-[#5c4033]/50"
                        >
                          {lang === 'fr' ? "Ouvrir l'Album" : "Open the Album"}
                        </button>
                      </div>
                    ) : currentAlbumPage <= filteredGalleries.length ? (() => {
                      /* PAGES 1 to N: GALLERY SPREADS */
                      const galleryIndex = currentAlbumPage - 1;
                      const gallery = filteredGalleries[galleryIndex];
                      if (!gallery) return null;

                      return (
                        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-0 sm:gap-0 p-0 sm:p-0 rounded relative shadow-2xl overflow-hidden border-2 border-[#5c4033]/40">
                          {/* Left Page: Photo Print View */}
                          <div 
                            className="flex flex-col justify-between p-6 relative transition-all duration-300 text-[#1e1712] shadow-[inset_-10px_0_20px_rgba(0,0,0,0.15)] border-r border-[#5c4033]/30"
                            style={{
                              backgroundColor: '#e6d5b8',
                              backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")',
                              backgroundSize: 'auto',
                              backgroundRepeat: 'repeat'
                            }}
                          >
                            <div className="absolute top-3 right-3 w-10 h-10 rounded-full border border-red-800/20 flex items-center justify-center text-red-800/40 text-[8px] font-serif uppercase tracking-tighter font-extrabold rotate-12 select-none">
                              Proven
                            </div>

                            {/* Main clickable Image to open full lightbox */}
                            <div 
                              onClick={() => openGallery(gallery)}
                              className="relative aspect-[4/3] sm:aspect-square w-full overflow-hidden bg-black border border-black/10 shadow-md group cursor-pointer"
                            >
                              <img 
                                src={gallery.coverUrl} 
                                alt={gallery.title}
                                referrerPolicy="no-referrer"
                                className="w-full h-full object-cover transition-all duration-500 sepia-[20%] contrast-105 group-hover:sepia-0"
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <span className="text-white text-xs font-mono uppercase tracking-widest border border-white/30 px-3 py-1 bg-black/50 backdrop-blur-sm">
                                  {lang === 'fr' ? 'Découvrir la Galerie' : 'Explore Gallery'}
                                </span>
                              </div>
                            </div>

                            <div className="mt-4 pt-3 border-t border-black/10 text-center">
                              <p className="italic text-xs text-zinc-800 font-serif">
                                "{gallery.title}"
                              </p>
                              <p className="text-[9px] font-mono uppercase tracking-widest text-zinc-600 mt-1">
                                {gallery.location} — {gallery.date}
                              </p>
                            </div>
                          </div>

                          {/* Right Page: Case Details & Interactive Media Strips */}
                          <div 
                            className="flex flex-col justify-between p-6 relative text-[#1e1712] shadow-[inset_10px_0_20px_rgba(0,0,0,0.15)] border-l border-white/20"
                            style={{
                              backgroundColor: '#e6d5b8',
                              backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")',
                              backgroundSize: 'auto',
                              backgroundRepeat: 'repeat'
                            }}
                          >
                            <div className="absolute inset-4 pointer-events-none" />
                            
                            <div className="relative z-10">
                              <div className="flex items-center gap-2 mb-3">
                                <Search className="w-4 h-4 text-[#5c4033]" />
                                <span className="text-[9px] font-mono uppercase tracking-widest text-[#5c4033] font-bold">
                                  {lang === 'fr' ? "Rapport d'Investigation" : "Archive Dossier"}
                                </span>
                              </div>
                              
                              <h4 className="text-xl font-bold border-b pb-2 mb-3 font-serif text-zinc-900 border-black/10">
                                {gallery.title}
                              </h4>
                              
                              <p className="text-xs leading-relaxed mb-4 italic text-zinc-800 font-serif">
                                "{gallery.description}"
                              </p>

                              {/* Interactive Strip: select other images in the gallery to open directly in the lightbox */}
                              <div className="mt-4">
                                <span className="text-[9px] font-mono uppercase tracking-wider block mb-2 text-zinc-600 font-bold">
                                  {lang === 'fr' ? 'Échantillons Médias' : 'Media Samples'} :
                                </span>
                                
                                <div className="grid grid-cols-3 gap-2">
                                  {gallery.media.slice(1, 4).map((med, idx) => (
                                    <div 
                                      key={med.id}
                                      onClick={() => {
                                        setActiveGallery(gallery);
                                        setActiveMediaIndex(idx + 1); // open exact clicked item (+1 because we skipped cover)
                                      }}
                                      className="relative aspect-[4/3] bg-black border border-black/10 shadow-md rounded-none overflow-hidden cursor-pointer group/strip"
                                    >
                                      <img 
                                        src={med.url} 
                                        alt={med.alt} 
                                        referrerPolicy="no-referrer"
                                        className="w-full h-full object-cover group-hover/strip:scale-108 transition-all duration-300 brightness-90 group-hover/strip:brightness-100 sepia-[20%]"
                                      />
                                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/strip:opacity-100 bg-black/40 transition-opacity">
                                        <Sparkles className="w-3.5 h-3.5 text-[#5c4033]" />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                            
                            <div className="border-t pt-3 flex items-center justify-between text-[9px] font-mono text-zinc-600 relative z-10 border-black/10 font-bold">
                              <span>CASE REPORT #{String(galleryIndex + 1).padStart(2, '0')}</span>
                              <span className="flex items-center gap-1 text-[#5c4033] font-bold">
                                <Heart className="w-3 h-3 text-red-700 fill-red-700 animate-pulse" /> {gallery.location}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })() : (
                      /* PAGE N+1: THE END COVER */
                      <div 
                        className="w-full h-full flex flex-col items-center justify-center text-center p-6 sm:p-10 relative rounded min-h-[420px] border-[#3e2723]/50 text-[#1e1712] shadow-[inset_0_0_60px_rgba(0,0,0,0.5)]"
                        style={{
                          backgroundColor: '#e6d5b8',
                          backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")',
                          backgroundSize: 'auto',
                          backgroundRepeat: 'repeat'
                        }}
                      >
                        <div className="absolute inset-4 border-2 rounded pointer-events-none border-[#5c4033]/30" />
                        <BookOpen className="w-10 h-10 mb-6 opacity-80 text-[#5c4033]" />
                        <span className="text-[10px] font-mono text-[#5c4033] uppercase tracking-[0.3em] mb-2 font-bold">
                          {lang === 'fr' ? 'Clôture des Chroniques' : 'End of Chronicles'}
                        </span>
                        <h3 className="text-xl sm:text-2xl font-serif tracking-wider mb-4 leading-tight">
                          {lang === 'fr' ? 'VOTRE SAGA COMMENCE ICI' : 'YOUR EPIC STORY BEGINS HERE'}
                        </h3>
                        <div className="w-24 h-[1px] bg-[#5c4033]/60 my-3" />
                        <p className="text-xs sm:text-sm max-w-md px-6 leading-relaxed mb-8 font-serif text-zinc-800 font-medium">
                          {lang === 'fr'
                            ? "Chaque reportage photo ou vidéo est conçu comme une œuvre historique, un coffret de souvenirs d'une valeur inestimable. N'hésitez pas à nous contacter pour raconter votre histoire."
                            : "Each photography and cinema story is crafted as a priceless historical piece. Feel free to contact our studio to tell your unique story."}
                        </p>
                        
                        <div className="flex flex-wrap gap-4 justify-center">
                          <button
                            onClick={() => {
                              setFlipDirection('prev');
                              setCurrentAlbumPage(0);
                            }}
                            className="px-5 py-2 border text-xs font-serif uppercase tracking-widest hover:bg-[#1e1712] hover:text-[#f4ebd9] transition-all cursor-pointer border-[#5c4033]/50 text-[#1e1712] font-semibold"
                          >
                            {lang === 'fr' ? "Recommencer la Lecture" : "Restart Album"}
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Book Controls / Navigation */}
              <div className={`mt-8 pt-5 border-t flex flex-wrap items-center justify-between gap-4 select-none relative z-30 ${
                theme === 'sherlock-wedding' ? 'border-[#dfa95c]/20 bg-[#17110c]/90 p-4 rounded-lg' : 'border-theme-border bg-theme-card/90 backdrop-blur-md p-4 rounded-lg'
              }`}>
                <button
                  onClick={() => {
                    if (isPageFlipping) return;
                    setFlipDirection('prev');
                    setCurrentAlbumPage(Math.max(0, currentAlbumPage - 1));
                  }}
                  className={`px-4 py-2.5 border text-xs font-mono tracking-wider uppercase flex items-center gap-2 cursor-pointer transition-all rounded ${
                    currentAlbumPage === 0 || isPageFlipping
                      ? 'text-zinc-600 border-zinc-800 cursor-not-allowed opacity-30 bg-black/20' 
                      : 'bg-amber-400/10 border-amber-400/40 text-amber-300 hover:bg-amber-400/20 hover:border-amber-400 shadow-md hover:scale-105'
                  }`}
                  disabled={currentAlbumPage === 0 || isPageFlipping}
                >
                  <ChevronLeft className="w-4 h-4" /> {lang === 'fr' ? 'Page Précédente' : 'Previous Page'}
                </button>
                
                <span className="text-xs font-mono text-zinc-300 font-bold px-3 py-1.5 bg-black/60 border border-white/10 rounded-full shadow-inner">
                  PAGE {currentAlbumPage + 1} / {filteredGalleries.length + 2}
                </span>

                <button
                  onClick={() => {
                    if (isPageFlipping) return;
                    setFlipDirection('next');
                    setCurrentAlbumPage(Math.min(filteredGalleries.length + 1, currentAlbumPage + 1));
                  }}
                  className={`px-4 py-2.5 border text-xs font-mono tracking-wider uppercase flex items-center gap-2 cursor-pointer transition-all rounded ${
                    currentAlbumPage === filteredGalleries.length + 1 || isPageFlipping
                      ? 'text-zinc-600 border-zinc-800 cursor-not-allowed opacity-30 bg-black/20' 
                      : 'bg-amber-400/10 border-amber-400/40 text-amber-300 hover:bg-amber-400/20 hover:border-amber-400 shadow-md hover:scale-105'
                  }`}
                  disabled={currentAlbumPage === filteredGalleries.length + 1 || isPageFlipping}
                >
                  {lang === 'fr' ? 'Page Suivante' : 'Next Page'} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Galleries Bento Grid */}
        {displayMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode="popLayout">
              {filteredGalleries.map((gallery, index) => (
                <motion.div
                  key={gallery.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group relative cursor-pointer overflow-hidden bg-theme-card border border-theme-border shadow-2xl h-[450px]"
                  onClick={() => openGallery(gallery)}
                >
                  {/* Background Zoom Image */}
                  <div className="absolute inset-0">
                    <img
                      src={gallery.coverUrl || undefined}
                      alt={gallery.title}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out brightness-90 group-hover:brightness-75"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-theme-bg via-theme-bg/25 to-transparent" />
                  </div>

                  {/* Badges */}
                  <div className="absolute top-4 left-4 z-10 flex gap-2">
                    <span className="px-3 py-1 bg-theme-bg/80 backdrop-blur-md border border-theme-border text-[9px] uppercase tracking-widest font-mono text-theme-accent rounded-none">
                      {gallery.category === 'chateau'
                        ? (lang === 'fr' ? '🏰 Château' : '🏰 Castle')
                        : gallery.category === 'domaine'
                          ? (lang === 'fr' ? '🍇 Domaine' : '🍇 Estate')
                          : (lang === 'fr' ? '✨ Mariage' : '✨ Wedding')}
                    </span>
                    <span className="px-3 py-1 bg-theme-bg/80 backdrop-blur-md border border-theme-border text-[9px] uppercase tracking-widest font-mono text-theme-secondary rounded-none flex items-center gap-1">
                      {gallery.media.length} {lang === 'fr' ? 'Médias' : 'Media'}
                    </span>
                  </div>

                {/* Gallery Info Bottom Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10 flex flex-col justify-end transform translate-y-3 group-hover:translate-y-0 transition-transform duration-500">
                  <div className="flex items-center gap-1.5 text-xs text-theme-accent font-mono mb-2">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{gallery.location}</span>
                  </div>

                  <h3 className="text-xl font-serif text-theme-text font-semibold mb-2 group-hover:text-theme-accent transition-colors">
                    {gallery.title}
                  </h3>

                  <p className="text-xs text-theme-secondary line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 mb-4">
                    {gallery.description}
                  </p>

                  <div className="flex items-center justify-between pt-2 border-t border-theme-border opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <span className="text-[10px] uppercase font-mono tracking-wider text-theme-secondary flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" /> {gallery.date}
                    </span>
                    <span className="text-xs font-mono uppercase tracking-widest text-theme-accent flex items-center gap-1 font-semibold">
                      {t('portfolio_view_btn')} <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

        {/* If no galleries found */}
        {filteredGalleries.length === 0 && (
          <div className="text-center py-20 border border-dashed border-theme-border">
            <p className="text-theme-secondary text-sm font-mono">
              {lang === 'fr'
                ? "Aucun reportage disponible dans cette catégorie pour le moment."
                : "No reports available in this category at the moment."}
            </p>
          </div>
        )}

        {/* High-End Immersive Lightbox Modal */}
        <AnimatePresence>
          {activeGallery && activeMedia && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] bg-black/98 backdrop-blur-2xl flex flex-col select-none cursor-pointer"
              id="lightbox-container"
              onClick={(e) => {
                const isMediaOrControl = (e.target as HTMLElement).closest('img, video, button, a');
                if (!isMediaOrControl) {
                  closeGallery();
                }
              }}
            >
              {/* Floating Mobile Close Button (Ensures accessibility on iOS Safari viewport shifts) */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeGallery();
                }}
                className="md:hidden absolute top-4 right-4 z-[10002] p-3.5 bg-black/85 border border-white/10 text-white rounded-full flex items-center justify-center cursor-pointer shadow-2xl active:scale-90"
                id="lightbox-mobile-floating-close"
                title={t('portfolio_close')}
              >
                <X className="w-6 h-6 text-theme-accent" />
              </button>

              {/* Lightbox Header */}
              <div 
                className="h-20 px-4 md:px-6 border-b border-white/10 flex items-center justify-between bg-black/80 z-20"
              >
                <div className="min-w-0 flex-1 pr-4">
                  <h4 className="text-[10px] font-serif text-theme-accent italic leading-none mb-1">
                    {t('portfolio_ex_reportage')}
                  </h4>
                  <p className="text-sm md:text-base font-mono text-white font-bold truncate">{activeGallery.title}</p>
                </div>

                <div className="flex items-center gap-3 md:gap-6 flex-shrink-0">
                  {/* Star Favorite Toggle Button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onToggleFavoriteMedia && activeGallery && activeMedia) {
                        onToggleFavoriteMedia(activeGallery.id, activeMedia.id);
                      }
                    }}
                    className={`px-3 py-1.5 border text-xs font-mono uppercase tracking-wider flex items-center gap-1.5 transition-all shadow-md cursor-pointer ${
                      activeMedia?.isFavorite
                        ? 'bg-black/90 border-amber-400 text-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.3)]'
                        : 'bg-white/5 border-white/20 text-zinc-300 hover:text-amber-300 hover:border-amber-400'
                    }`}
                    title={activeMedia?.isFavorite ? "Retirer des Chefs-d'œuvre ⭐" : "Ajouter aux Chefs-d'œuvre ⭐"}
                  >
                    <Star className={`w-4 h-4 ${activeMedia?.isFavorite ? 'fill-amber-400 text-amber-400' : ''}`} />
                    <span className="hidden sm:inline">
                      {activeMedia?.isFavorite ? "Chef-d'œuvre ⭐" : "Favori ⭐"}
                    </span>
                  </button>

                  {/* Media Counter */}
                  <span className="text-xs font-mono text-zinc-400 tracking-widest bg-white/5 px-2.5 py-1">
                    {activeMediaIndex! + 1} / {activeGallery.media.length}
                  </span>

                  {/* Close Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      closeGallery();
                    }}
                    className="p-2.5 md:px-4 md:py-2 bg-white/10 hover:bg-theme-accent hover:text-theme-bg border border-white/20 text-white font-mono text-xs uppercase tracking-widest transition-all duration-300 transform hover:scale-105 flex items-center gap-2 cursor-pointer shadow-lg rounded-none"
                    id="lightbox-close-btn"
                    aria-label="Fermer la galerie et le lecteur média"
                    title={t('portfolio_close')}
                  >
                    <X className="w-5 h-5 text-theme-accent" />
                    <span className="hidden md:inline">{t('portfolio_close')}</span>
                  </button>
                </div>
              </div>

              {/* Main Media Slide Area */}
              <div 
                className="flex-1 relative flex items-center justify-center p-4 md:p-8"
              >
                {/* Previous Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevMedia();
                  }}
                  className="absolute left-4 md:left-8 z-10 p-3 rounded-full bg-black/60 hover:bg-theme-accent hover:text-theme-bg border border-white/10 text-white transition-all transform hover:scale-105 cursor-pointer"
                  id="lightbox-prev-btn"
                  aria-label="Afficher le média précédent"
                  title={t('portfolio_prev')}
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                {/* Media Container */}
                <div 
                  className="max-w-5xl max-h-[70vh] w-full h-full flex items-center justify-center relative"
                >
                  <AnimatePresence mode="sync">
                    <motion.div
                      key={activeMedia.id}
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.97 }}
                      transition={{ duration: 0.3 }}
                      className="w-full h-full flex items-center justify-center"
                    >
                      {activeMedia.type === 'video' ? (
                        <div className="relative w-full max-h-full aspect-video flex items-center justify-center bg-black">
                          <video
                            controls
                            autoPlay
                            playsInline
                            preload="metadata"
                            className="w-full h-full object-contain max-h-[70vh] border border-white/10 shadow-2xl"
                          >
                            {activeMedia.h265Url && activeMedia.h265Url.trim() !== '' && (
                              <source src={activeMedia.h265Url} type='video/mp4; codecs="hevc, mp4a.40.2"' />
                            )}
                            {activeMedia.h264Url && activeMedia.h264Url.trim() !== '' && (
                              <source src={activeMedia.h264Url} type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"' />
                            )}
                            {activeMedia.url && activeMedia.url.trim() !== '' && (
                              <source src={activeMedia.url} type="video/mp4" />
                            )}
                          </video>
                        </div>
                      ) : (
                        <img
                          src={activeMedia.url || undefined}
                          alt={activeMedia.alt}
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          className="max-w-full max-h-full object-contain border border-white/10 shadow-2xl rounded-none select-none pointer-events-auto"
                        />
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Next Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextMedia();
                  }}
                  className="absolute right-4 md:right-8 z-10 p-3 rounded-full bg-black/60 hover:bg-theme-accent hover:text-theme-bg border border-white/10 text-white transition-all transform hover:scale-105 cursor-pointer"
                  id="lightbox-next-btn"
                  aria-label="Afficher le média suivant"
                  title={t('portfolio_next')}
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>

              {/* Lightbox Footer (Captions & Tags) */}
              <div 
                className="p-4 md:p-6 bg-black/40 border-t border-white/10 text-center flex flex-col items-center justify-center min-h-[80px]"
              >
                {activeMedia.caption && (
                  <p className="text-zinc-200 font-serif italic text-sm md:text-base mb-2">
                    "{activeMedia.caption}"
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 border border-white/10 text-[9px] font-mono text-zinc-400 bg-white/5">
                    {activeMedia.type === 'video' ? (
                      <span className="flex items-center gap-1"><VideoIcon className="w-3 h-3 text-red-400" /> {t('portfolio_film_hdr')}</span>
                    ) : (
                      <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3 text-theme-accent" /> {t('portfolio_photo_num')}</span>
                    )}
                  </span>
                  <p className="text-xs font-mono text-zinc-400 hidden md:block">
                    {t('portfolio_seo_alt')} <span className="text-zinc-300 italic">{activeMedia.alt}</span>
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </section>
  );
}
